// Carteiras USDT do lojista (`spec/30-scalefy.md` §5.7, WEB-134): pra onde vai o saque
// internacional. No máximo uma carteira ativa por rede; a nova entra em carência de 48 h.
//
// Quem tem `useQuery`/`useMutation` é esta rota; os componentes de `features/crypto-wallets`
// recebem dado por prop (mesma divisão de `app.settings.pix-keys.index.tsx`). Cadastrar e
// remover pedem step-up, resolvido por `api/client.ts`.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useState } from 'react';
import { toast } from 'sonner';
import { useMode } from '../app/mode.tsx';
import { EmptyState } from '../components/app/empty-state.tsx';
import { ErrorState } from '../components/app/error-state.tsx';
import { ListSkeleton } from '../components/app/list-skeleton.tsx';
import { PageHeader } from '../components/app/page-header.tsx';
import { WalletCards } from '../components/icons/index.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import { CreateWalletDrawer } from '../features/crypto-wallets/create-wallet-drawer.tsx';
import { freeNetworks, NETWORKS } from '../features/crypto-wallets/model.ts';
import {
  CRYPTO_WALLETS_KEY,
  type CreateCryptoWalletBody,
  type CryptoWallet,
  createCryptoWallet,
  cryptoWalletsQueryOptions,
  removeCryptoWallet,
} from '../features/crypto-wallets/queries.ts';
import { RemoveWalletDrawer } from '../features/crypto-wallets/remove-wallet-drawer.tsx';
import { WalletsTable } from '../features/crypto-wallets/wallets-table.tsx';

function CryptoWallets() {
  const { mode } = useMode();
  const queryClient = useQueryClient();
  const wallets = useQuery(cryptoWalletsQueryOptions(mode));

  const [createOpen, setCreateOpen] = useState(false);
  const [toRemove, setToRemove] = useState<CryptoWallet | undefined>(undefined);

  const refresh = (): Promise<void> =>
    queryClient.invalidateQueries({ queryKey: [CRYPTO_WALLETS_KEY] });

  const create = useMutation({
    mutationFn: (body: CreateCryptoWalletBody) => createCryptoWallet(mode, body),
    onSuccess: async () => {
      setCreateOpen(false);
      toast.success('Carteira cadastrada', {
        description: 'Ela passa a receber saque depois da carência de 48 horas.',
      });
      await refresh();
    },
  });

  const remove = useMutation({
    mutationFn: (wallet: CryptoWallet) => removeCryptoWallet(mode, wallet.id),
    onSuccess: async () => {
      setToRemove(undefined);
      toast.success('Carteira removida');
      await refresh();
    },
  });

  const rows = wallets.data ?? [];
  const free = freeNetworks(rows);
  // Erro só quando não há lista: um flake no refetch de fundo não troca a tela carregada.
  const isError = wallets.data === undefined && wallets.isError;

  const openCreate = (): void => {
    create.reset();
    setCreateOpen(true);
  };

  return (
    <>
      <PageHeader
        title="Carteiras USDT"
        description={
          <>
            Pra onde vai o seu{' '}
            <Link
              to="/app/international-withdrawals"
              className="font-medium text-foreground underline underline-offset-4"
            >
              saque internacional
            </Link>
            . Uma carteira por rede.
          </>
        }
      >
        {wallets.isSuccess && (
          <Button disabled={free.length === 0} onClick={openCreate}>
            Cadastrar carteira
          </Button>
        )}
      </PageHeader>

      {isError ? (
        <ErrorState
          error={wallets.error}
          onRetry={() => {
            void wallets.refetch();
          }}
        />
      ) : wallets.isPending ? (
        <div role="status" aria-busy="true" aria-label="Carregando">
          <ListSkeleton rows={2} />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={WalletCards}
          title="Nenhuma carteira USDT cadastrada"
          description="O saque internacional é enviado em USDT pra uma carteira sua, na rede Tron (TRC20) ou BSC (BEP20). Cadastre uma pra poder sacar."
          action={<Button onClick={openCreate}>Cadastrar carteira</Button>}
        />
      ) : (
        <Card className="rise-in gap-0 overflow-hidden py-0" style={{ animationDelay: '40ms' }}>
          <div className="flex flex-col gap-0.5 px-5 py-4">
            <h2 className="text-15 font-semibold tracking-[-0.01em] text-foreground">
              Suas carteiras
            </h2>
            <span className="text-13 text-muted-foreground">
              {rows.length} de {NETWORKS.length} redes
              {free.length === 0 && ' · pra trocar o endereço de uma rede, remova a carteira atual'}
            </span>
          </div>
          <WalletsTable wallets={rows} onRemove={setToRemove} />
        </Card>
      )}

      {wallets.isSuccess && (
        <p className="text-12 text-muted-foreground">
          <span className="font-medium text-foreground">
            Cadastrar e remover pedem confirmação de identidade.
          </span>{' '}
          Senha atual ou código do app autenticador, e você recebe um e-mail a cada mudança.
        </p>
      )}

      <CreateWalletDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        free={free}
        isPending={create.isPending}
        error={create.error}
        onSubmit={(body) => {
          create.mutate(body);
        }}
      />

      <RemoveWalletDrawer
        wallet={toRemove}
        onOpenChange={(open) => {
          if (!open) {
            setToRemove(undefined);
            remove.reset();
          }
        }}
        isPending={remove.isPending}
        error={remove.error}
        onConfirm={(wallet) => {
          remove.mutate(wallet);
        }}
      />
    </>
  );
}

export const Route = createFileRoute('/app/settings/crypto-wallets/')({
  component: CryptoWallets,
});
